import axios from 'axios';
import { getToken } from './authToken';

// Get backend URL from environment
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY_BASE = 1000; // 1 second base delay

// Helper function for exponential backoff
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth and logging
api.interceptors.request.use(
  (config) => {
    console.log(`🔗 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Add retry count to config if not present
    if (!config.retryCount) {
      config.retryCount = 0;
    }
    
    // Add authorization header if token exists
    const token = getToken();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`🔐 Added authorization header to ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    return config;
  },
  (error) => {
    console.error('🚨 API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and retry logic
api.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    return response;
  },
  async (error) => {
    const { config, response } = error;
    
    console.error('🚨 API Response Error:', response?.status, response?.data || error.message);
    
    // Don't retry if no config or max retries reached
    if (!config || config.retryCount >= MAX_RETRIES) {
      return Promise.reject(error);
    }
    
    // Only retry on network errors or 5xx server errors
    const shouldRetry = !response || (response.status >= 500 && response.status < 600);
    
    if (shouldRetry) {
      config.retryCount = (config.retryCount || 0) + 1;
      
      // Calculate exponential backoff delay
      const delay = RETRY_DELAY_BASE * Math.pow(2, config.retryCount - 1);
      
      console.log(`🔄 Retrying request (attempt ${config.retryCount}/${MAX_RETRIES}) after ${delay}ms...`);
      
      // Wait before retrying
      await sleep(delay);
      
      // Retry the request
      return api(config);
    }
    
    // Handle common errors
    if (response?.status === 401) {
      // Handle unauthorized
      console.warn('🔐 Unauthorized access');
    } else if (response?.status >= 500) {
      // Handle server errors
      console.error('🔥 Server error - not retrying');
    }
    
    return Promise.reject(error);
  }
);

// Error wrapper for API calls
const withErrorHandling = async (apiCall, fallbackValue = null) => {
  try {
    return await apiCall();
  } catch (error) {
    console.error('API call failed:', error);
    
    // Return fallback value for non-critical errors
    if (error.response?.status === 404) {
      return fallbackValue;
    }
    
    // Re-throw for critical errors
    throw error;
  }
};

// Instance Management API
export const instancesAPI = {
  // Get all instances
  getAll: async () => {
    return withErrorHandling(
      async () => {
        const response = await api.get('/instances');
        return response.data; // Return full response object with instances property
      },
      { instances: [] } // Return object with empty array on error
    );
  },

  // Create new instance
  create: async (name) => {
    return withErrorHandling(async () => {
      const response = await api.post('/instances', { name });
      return response.data.instance;
    });
  },

  // Update instance
  update: async (id, data) => {
    return withErrorHandling(async () => {
      const response = await api.put(`/instances/${id}`, data);
      return response.data.instance;
    });
  },

  // Select instance as active (Phase 3.5)
  select: async (id) => {
    return withErrorHandling(async () => {
      const response = await api.patch(`/instances/${id}/select`, { action: 'select' });
      return response.data.instance;
    });
  },

  // Rename instance (Phase 3.5)
  rename: async (id, name) => {
    return withErrorHandling(async () => {
      const response = await api.patch(`/instances/${id}`, { name });
      return response.data.instance;
    });
  },

  // Delete instance (Phase 3.5)
  delete: async (id) => {
    return withErrorHandling(async () => {
      const response = await api.delete(`/instances/${id}`);
      return response.data;
    });
  },

  // Get entities for instance
  getEntities: async (instanceId) => {
    const response = await api.get(`/instances/${instanceId}/entities`);
    return response.data.entities || [];
  },

  // Get stories for instance
  getStories: async (instanceId) => {
    const response = await api.get(`/instances/${instanceId}/stories`);
    return response.data.stories || [];
  },

  // Get chapters for instance
  getChapters: async (instanceId) => {
    const response = await api.get(`/instances/${instanceId}/chapters`);
    return response.data.chapters || [];
  },

  // Get reels for instance
  getReels: async (instanceId) => {
    const response = await api.get(`/instances/${instanceId}/reels`);
    return response.data.reels || [];
  },
};

// Feed API
export const feedAPI = {
  // Get feed posts with pagination and source ordering
  getFeed: async (instanceId, cursor = null, limit = 20, type = 'post', chapterIndex = null, sortBy = 'source_order') => {
    const params = { instanceId, limit, sortBy };
    if (cursor) params.cursor = cursor;
    if (type) params.type = type;
    if (chapterIndex !== null && chapterIndex !== undefined) params.chapterIndex = chapterIndex;
    
    const response = await api.get('/feed', { params });
    return {
      posts: response.data.posts || [],
      nextCursor: response.data.next_cursor,
      hasMore: response.data.has_more || false,
      validationStats: response.data.validation_stats
    };
  },

  // Get entity profile
  getEntity: async (entityId) => {
    const response = await api.get(`/entities/${entityId}`);
    return response.data; // Return full response with entity and posts
  },

  // Get chapters for an instance
  getChapters: async (instanceId) => {
    const response = await api.get(`/instances/${instanceId}/chapters`);
    return response.data.profiles;
  },

  // Search within instance
  search: async (instanceId, query, type = 'entities', limit = 20) => {
    const params = { instanceId, q: query, type, limit };
    const response = await api.get('/search', { params });
    return response.data.results || [];
  },
};

// Interactions API
export const interactionsAPI = {
  // Create interaction (like, save, comment)
  create: async (postId, type, commentText = null) => {
    const data = { post_id: postId, type };
    if (commentText) data.comment_text = commentText;

    const response = await api.post('/interactions', data);

   
    return response.data;
  },

  // Toggle save (quick save/unsave)
  toggleSave: async (postId) => {
    const response = await api.post('/interactions', {
      post_id: postId,
      type: 'save'
    });
    return response.data;
  },

  // Follow/unfollow entity
  follow: async (entityId) => {
    const response = await api.post('/follows', { entity_id: entityId });
    return response.data;
  },

  // Get saved posts (enhanced with collection filtering)
  getSaved: async (options = {}) => {
    const {
      instanceId,
      collection,
      includeCollections = false,
      limit = 50,
      cursor
    } = typeof options === 'string' ? { instanceId: options } : options;

    const params = { limit };
    if (instanceId) params.instance_id = instanceId;
    if (collection) params.collection = collection;
    if (includeCollections) params.include_collections = '1';
    if (cursor) params.cursor = cursor;

    const response = await api.get('/users/saved', { params });
    return {
      posts: response.data.saved_posts || [],
      hasMore: response.data.has_more || false,
      nextCursor: response.data.next_cursor
    };
  },

  // Get post comments
  getComments: async (postId, limit = 50) => {
    const response = await api.get(`/posts/${postId}/comments`, {
      params: { limit }
    });
    return response.data.comments || [];
  },

  // ==========================================
  // COLLECTIONS MANAGEMENT
  // ==========================================

  // Get user collections
  getCollections: async () => {
    const response = await api.get('/collections');
    return response.data.collections || [];
  },
 
  // Create new collection
  createCollection: async (name) => {
    const response = await api.post('/collections', { name });
    return response.data;
  },

  // Rename collection
  renameCollection: async (oldName, newName) => {
    const response = await api.patch(`/collections/${encodeURIComponent(oldName)}`, {
      new_name: newName
    });
    return response.data;
  },

  // Set collection cover
  setCollectionCover: async (name, coverUrl) => {
    const response = await api.patch(`/collections/${encodeURIComponent(name)}`, {
      cover_url: coverUrl
    });
    return response.data;
  },

  // Delete collection
  deleteCollection: async (name) => {
    const response = await api.delete(`/collections/${encodeURIComponent(name)}`);
    return response.data;
  },

  // Add post to collections
  addToCollections: async (postId, collections) => {
    const response = await api.post('/collections/save', {
      post_id: postId,
      collections
    });
    return response.data;
  },

  // Add multiple posts to collections (bulk)
  addManyToCollections: async (postIds, collections) => {
    const response = await api.post('/collections/save', {
      post_ids: postIds,
      collections
    });
    return response.data;
  },

  // Remove post from collections
  removeFromCollections: async (postId, collections) => {
    const response = await api.delete('/collections/save', {
      data: { post_id: postId, collections }
    });
    return response.data;
  },

  // Remove multiple posts from collections (bulk)
  removeManyFromCollections: async (postIds, collections) => {
    const response = await api.delete('/collections/save', {
      data: { post_ids: postIds, collections }
    });
    return response.data;
  },

  // Remove post from all collections (unsave completely)
  removeFromAllCollections: async (postId) => {
    const response = await api.delete('/collections/save', {
      data: { post_id: postId, remove_all: true }
    });
    return response.data;
  },
};

// Discover API
export const discoverAPI = {
  search: async ({
    query,
    limit = 20,
    offset = 0,
    instances = [],
    slideTypes = [],
    languages = [],
    difficulties = [],
    dateFrom,
    dateTo,
  } = {}) => {
    const params = {
      q: query,
      limit,
      offset,
    };

    if (instances && instances.length) {
      params.instances = instances.join(',');
    }

    if (slideTypes && slideTypes.length) {
      params.slide_type = slideTypes.join(',');
    }

    if (languages && languages.length) {
      params.language = languages.join(',');
    }

    if (difficulties && difficulties.length) {
      params.difficulty = difficulties.join(',');
    }

    if (dateFrom) {
      params.date_from = dateFrom;
    }

    if (dateTo) {
      params.date_to = dateTo;
    }

    const response = await api.get('/discover/search', { params });
    return response.data;
  },
};

// Document Processing API
export const documentsAPI = {
  // Upload PDF file
  upload: async (instanceId, file, onProgress = null) => {
    const userId = localStorage.getItem('user_id');
    if (!userId) {
      throw new Error('User not authenticated. Please log in.');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('instance_id', instanceId);
    formData.append('filename', file.name);
    formData.append('user_id', userId);

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      };
    }

    const response = await api.post('/documents/upload', formData, config);
    return response.data;
  },

  // Start document processing
  process: async (documentId) => {
    const response = await api.post(`/documents/process/${documentId}`);
    return response.data;
  },

  // Get documents for instance
  getForInstance: async (instanceId) => {
    const response = await api.get(`/instances/${instanceId}/documents`);
    return response.data.documents || [];
  },
};

// Jobs API for background processing
export const jobsAPI = {
  // Get job status
  getStatus: async (jobId) => {
    const response = await api.get(`/jobs/${jobId}`);
    return response.data;
  },

  // Get jobs for instance
  getForInstance: async (instanceId) => {
    const response = await api.get(`/instances/${instanceId}/jobs`);
    return response.data.jobs || [];
  },

  // Cancel job
  cancel: async (jobId) => {
    const response = await api.post(`/jobs/${jobId}/cancel`);
    return response.data;
  },

  // Retry failed job
  retry: async (jobId) => {
    const response = await api.post(`/jobs/${jobId}/retry`);
    return response.data;
  },

  // Create WebSocket connection for real-time updates
  createWebSocket: (jobId, onMessage, onError = null) => {
    const wsUrl = `${BACKEND_URL}/api/jobs/${jobId}/stream`.replace('http', 'ws');
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log(`🔌 WebSocket connected for job: ${jobId}`);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch (error) {
        console.error('🚨 WebSocket message parse error:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('🚨 WebSocket error:', error);
      if (onError) onError(error);
    };

    ws.onclose = () => {
      console.log(`🔌 WebSocket disconnected for job: ${jobId}`);
    };

    return ws;
  },
};

// Health check
export const healthAPI = {
  check: async () => {
    const response = await api.get('/health');
    return response.data;
  },
};

// Error handling utilities
export const handleApiError = (error, fallbackMessage = 'Something went wrong') => {
  if (error.response?.data?.error) {
    return error.response.data.error;
  } else if (error.message) {
    return error.message;
  } else {
    return fallbackMessage;
  }
};

// Loading state utilities
export const createLoadingState = () => ({
  loading: false,
  error: null,
  data: null,
});

export const withLoading = async (loadingState, asyncOperation) => {
  loadingState.loading = true;
  loadingState.error = null;
  
  try {
    const result = await asyncOperation();
    loadingState.data = result;
    return result;
  } catch (error) {
    loadingState.error = handleApiError(error);
    throw error;
  } finally {
    loadingState.loading = false;
  }
};


// Update your profileAPI section in api.js
export const profileAPI = {
  // Update user profile - use PUT /profile
  updateProfile: async (profileData) => {
    console.log('🔄 Updating profile with:', profileData);
    const response = await api.put('/auth/profile', profileData);
    console.log('✅ Profile update response:', response.data);
    return response.data;
  },

  // Get user profile - use GET /me (not /auth/me)
  getProfile: async () => {
    console.log('🔄 Getting user profile from /me...');
    const response = await api.get('/auth/me');
    console.log('✅ Profile response:', response.data);
    return response.data;
  },

  updatePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/auth/update-password', { 
      currentPassword, 
      newPassword 
    });
    return response.data;
  },

};

// Export api as apiClient for compatibility
export const apiClient = api;

// Export API base URL for other services
export const API_BASE_URL = API_BASE;

export default api;
